import React from 'react';
import { H2, P, UL, LI, Code, Pre, Callout, FeatureLink } from './_shared';

export default {
  slug: 'webhooks',
  title: 'Webhooks',
  category: 'integrations',
  summary: 'HMAC-signed event delivery with automatic retries. Subscribe in Integrations → Webhooks.',
  searchTerms: ['webhook', 'event', 'hmac', 'signature', 'subscribe'],
  related: ['api-keys', 'public-api'],
  Body: () => (
    <>
      <P>
        Webhooks push events to your endpoint as they happen — useful when
        you'd otherwise poll the API every minute.
      </P>

      <H2>Subscribing</H2>
      <P>
        <FeatureLink to="/settings/integrations">Integrations → Webhooks → New</FeatureLink>.
        Enter your endpoint URL, pick the events you want, save. You'll see
        a signing secret <strong>once</strong> — copy it into your endpoint's
        env vars.
      </P>

      <H2>Event catalog (subset)</H2>
      <UL>
        <LI><Code>client.created</Code>, <Code>client.updated</Code>, <Code>client.deleted</Code></LI>
        <LI><Code>deal.created</Code>, <Code>deal.stage_changed</Code>, <Code>deal.closed</Code></LI>
        <LI><Code>folder.created</Code>, <Code>folder.status_changed</Code>, <Code>folder.envelope_sent</Code></LI>
        <LI><Code>appointment.created</Code>, <Code>appointment.completed</Code>, <Code>appointment.no_show</Code></LI>
      </UL>

      <H2>Verifying the signature</H2>
      <P>
        Every delivery includes <Code>X-RealtyFlows-Signature</Code>: a
        hex-encoded HMAC-SHA256 of the raw request body using your signing
        secret. In Node:
      </P>
      <Pre>{`const crypto = require('crypto');

function verify(req, secret) {
  const sent = req.headers['x-realtyflows-signature'];
  const expected = crypto
    .createHmac('sha256', secret)
    .update(req.rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(sent), Buffer.from(expected)
  );
}`}</Pre>

      <H2>Retries</H2>
      <P>
        Failed deliveries (non-2xx response or timeout {'>'} 10s) retry with
        exponential backoff for 24 hours. After 10 consecutive failures the
        subscription auto-disables and you get an alert email.
      </P>

      <Callout kind="tip">
        Use a service like webhook.site to inspect the payload shape during
        development before pointing the URL at your real endpoint.
      </Callout>
    </>
  ),
};
